/* __next_internal_client_entry_do_not_use__ ShowroomAnimatedCategoryTile auto */ import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_array } from "@swc/helpers/_/_to_array";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Row } from "@faire/web--source/slate/Layout";
import { Typography } from "@faire/web--source/slate/Typography";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled, { css, keyframes } from "styled-components";
var INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION = 1000;
var IMAGE_DELAY_DURATION = 2000;
var DISSOLVE_DURATION = 500;
var SINGLE_IMAGE_SHOWING_TIME = IMAGE_DELAY_DURATION + DISSOLVE_DURATION;
var INTRO_DURATION = IMAGE_DELAY_DURATION + INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION + DISSOLVE_DURATION;
var ShowroomAnimatedCategoryTileBase = function(param) {
    var images = param.images;
    var isSSR = useIsSSR();
    var _useState = _sliced_to_array(useState(true), 2), isImageLoading = _useState[0], setIsImageLoading = _useState[1];
    var onImageLoading = function() {
        setIsImageLoading(false);
    };
    var totalDuration = !(images === null || images === void 0 ? void 0 : images.length) ? 0 : SINGLE_IMAGE_SHOWING_TIME * (images.length - 1);
    var fadeInPercent = "".concat(100 * DISSOLVE_DURATION / totalDuration, "%");
    var delayPercent = "".concat(100 * SINGLE_IMAGE_SHOWING_TIME / totalDuration, "%");
    var fadeOutPercent = "".concat(100 * (SINGLE_IMAGE_SHOWING_TIME + DISSOLVE_DURATION) / totalDuration, "%");
    var _ref = _to_array(images !== null && images !== void 0 ? images : []), fullImage = _ref[0], thinImages = _ref.slice(1);
    var _images_length;
    return /*#__PURE__*/ _jsxs(ImageWrapper, {
        children: [
            fullImage ? /*#__PURE__*/ _jsx(FullSizeImage, {
                src: optimizeImageUrl(fullImage, isSSR),
                $startAnimation: !isImageLoading,
                $shouldFadeOut: ((_images_length = images === null || images === void 0 ? void 0 : images.length) !== null && _images_length !== void 0 ? _images_length : 0) > 1,
                onLoad: onImageLoading
            }) : null,
            thinImages.map(function(image, index) {
                return /*#__PURE__*/ _jsx(ThinImage, {
                    src: optimizeImageUrl(image, isSSR),
                    $startAnimation: !isImageLoading,
                    $delayedStart: INTRO_DURATION + index * SINGLE_IMAGE_SHOWING_TIME,
                    $fadeInPercent: fadeInPercent,
                    $fadeOutPercent: fadeOutPercent,
                    $delayPercent: delayPercent,
                    $total: totalDuration,
                    width: "100%",
                    height: "100%"
                }, index);
            }),
            /*#__PURE__*/ _jsx(Overlay, {
                $startAnimation: !isImageLoading
            }),
            /*#__PURE__*/ _jsx(TextWrapper, {
                $startAnimation: !isImageLoading,
                children: /*#__PURE__*/ _jsxs(StyledTypography, {
                    color: Core.text.inverse,
                    variant: "subheadingSansMedium",
                    $startAnimation: !isImageLoading,
                    children: [
                        "SHOW",
                        /*#__PURE__*/ _jsx("br", {}),
                        "ROOM"
                    ]
                })
            })
        ]
    });
};
export var ShowroomAnimatedCategoryTile = observer(ShowroomAnimatedCategoryTileBase);
var overlayAnimation = keyframes([
    "0%{opacity:0.8;transform:translateX(0);}100%{opacity:1;transform:translateX(-50%);}"
]);
var textAnimation = keyframes([
    "0%{transform:scale(1);}100%{transform:scale(0.7);}"
]);
var textWrapperAnimation = keyframes([
    "0%{transform:translateX(0);}100%{transform:translateX(-25%);}"
]);
var fullSizeImageFadeInAnimation = keyframes([
    "0%{transform:scale(1.1) translateX(0);}100%{transform:scaleX(0.95) translateX(30%);}"
]);
var fullSizeImageFadeOutAnimation = keyframes([
    "0%{opacity:1;}100%{opacity:0;}"
]);
var thinImageAnimation = function(fadeIn, delay, fadeOut) {
    return keyframes([
        "0%{opacity:0;}",
        ",",
        "{opacity:1;}",
        ",100%{opacity:0;}"
    ], fadeIn, delay, fadeOut);
};
var Overlay = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-10862992-0"
})([
    "background-color:",
    ";width:100%;opacity:0.8;height:100%;position:absolute;top:0;left:0;",
    ""
], Core.surface.enabled, function(props) {
    return props.$startAnimation ? css([
        "animation:",
        " ",
        "ms ease-in ",
        "ms 1 forwards;"
    ], overlayAnimation, DISSOLVE_DURATION, INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION) : "";
});
var ImageWrapper = styled.div.withConfig({
    componentId: "sc-10862992-1"
})([
    "position:relative;width:100%;height:0;overflow:hidden;padding-top:calc(100% / 0.75);"
]);
var FullSizeImage = styled.img.withConfig({
    componentId: "sc-10862992-2"
})([
    "width:100%;transform:scale(1.1);object-fit:contain;position:absolute;top:0;left:0;",
    " ",
    ""
], function(props) {
    return props.$startAnimation && props.$shouldFadeOut ? css([
        "animation:",
        " ",
        "ms ease-in ",
        "ms 1 forwards,",
        " ",
        "ms ease-in ",
        "ms 1 forwards;"
    ], fullSizeImageFadeInAnimation, DISSOLVE_DURATION, INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION, fullSizeImageFadeOutAnimation, DISSOLVE_DURATION, INTRO_DURATION) : "";
}, function(props) {
    return props.$startAnimation && !props.$shouldFadeOut ? css([
        "animation:",
        " ",
        "ms ease-in ",
        "ms 1 forwards;"
    ], fullSizeImageFadeInAnimation, DISSOLVE_DURATION, INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION) : "";
});
var StyledTypography = styled(Typography).withConfig({
    componentId: "sc-10862992-3"
})([
    "&&&{font-size:20px;transform-origin:center;",
    "}"
], function(props) {
    return props.$startAnimation ? css([
        "animation:",
        " ",
        "ms ease-in ",
        "ms 1 forwards;"
    ], textAnimation, DISSOLVE_DURATION, INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION) : "";
});
var TextWrapper = styled(Row).attrs({
    align: "center",
    justify: "center"
}).withConfig({
    componentId: "sc-10862992-4"
})([
    "position:absolute;width:100%;height:100%;top:0;left:0;",
    ""
], function(props) {
    return props.$startAnimation ? css([
        "animation:",
        " ",
        "ms ease-in ",
        "ms 1 forwards;"
    ], textWrapperAnimation, DISSOLVE_DURATION, INTRO_FULL_IMAGE_WITH_OVERLAY_DURATION) : "";
});
var ThinImage = styled.img.withConfig({
    componentId: "sc-10862992-5"
})([
    "width:50%;height:100%;opacity:0;position:absolute;top:0;right:0;",
    ""
], function(props) {
    return props.$startAnimation ? css([
        "animation:",
        " ",
        "ms ease-in-out ",
        "ms infinite forwards;"
    ], thinImageAnimation(props.$fadeInPercent, props.$delayPercent, props.$fadeOutPercent), props.$total, props.$delayedStart) : "";
});
